// src/pages/BulkUpload.js
import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import api from '../utils/api';

const FIELDS = [
  { key: 'upn', label: 'UPN (١٣ ژمارە)', required: true },
  { key: 'full_name', label: 'ناوی فەرمانبەر', required: true },
  { key: 'national_id', label: 'ژمارەی نیشتیمانی (١١ ژمارە)', required: false },
  { key: 'rank', label: 'پیشە/پلە', required: false },
  { key: 'position', label: 'بواری کار', required: false },
  { key: 'ministry', label: 'وەزارەت', required: false },
  { key: 'directorate', label: 'بەرێوەبەرایەتی', required: false },
];

export default function BulkUpload() {
  const fileRef = useRef(null);
  const [step, setStep] = useState(1); // 1: pick file, 2: map columns, 3: preview/result
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [depts, setDepts] = useState([]);
  const [departmentId, setDepartmentId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/departments').then(r => setDepts(r.data));
  }, []);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        if (json.length < 2) {
          toast.error('فایلەکە بەتاڵە یان تەنها هێڵی سەردێڕی هەیە');
          return;
        }
        const hdrs = json[0].map(h => String(h).trim());
        const rows = json.slice(1).filter(r => r.some(cell => String(cell).trim() !== ''));

        setHeaders(hdrs);
        setRawRows(rows);

        // Try auto-matching by header name similarity
        const auto = {};
        const tryMatch = (key, patterns) => {
          const idx = hdrs.findIndex(h => patterns.some(p => h.toLowerCase().includes(p)));
          if (idx !== -1) auto[key] = idx;
        };
        tryMatch('upn', ['upn']);
        tryMatch('full_name', ['name', 'ناو', 'fullname']);
        tryMatch('national_id', ['national', 'نیشتیمانی', 'id card']);
        tryMatch('rank', ['rank', 'پیشە', 'پلە']);
        tryMatch('position', ['position', 'بوار']);
        tryMatch('ministry', ['ministry', 'وەزارەت']);
        tryMatch('directorate', ['directorate', 'بەرێوەبەرایەتی']);
        setMapping(auto);

        setStep(2);
      } catch (err) {
        toast.error('نەتوانرا فایلەکە بخوێنرێتەوە. دڵنیابە کە فایلی .xlsx یان .xls ـە');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const buildMappedRows = () => {
    return rawRows.map(r => {
      const obj = {};
      FIELDS.forEach(f => {
        const colIdx = mapping[f.key];
        obj[f.key] = colIdx !== undefined && colIdx !== '' ? String(r[colIdx] ?? '').trim() : '';
      });
      return obj;
    });
  };

  const mappedRows = step >= 2 ? buildMappedRows() : [];
  const validCount = mappedRows.filter(r => /^\d{13}$/.test(r.upn) && r.full_name).length;

  const canProceedMapping = mapping.upn !== undefined && mapping.full_name !== undefined && departmentId;

  const submit = async () => {
    if (!departmentId) return toast.error('تکایە بەش هەڵبژێرە');
    setSubmitting(true);
    try {
      const { data } = await api.post('/forms/bulk-import', {
        rows: mappedRows,
        department_id: departmentId,
      });
      setResult(data);
      setStep(3);
      toast.success('ئەپلۆد تەواو بوو!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'هەڵەیەک ڕووی دا');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(1);
    setFileName('');
    setHeaders([]);
    setRawRows([]);
    setMapping({});
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>📤 ئەپلۆدی بەکۆمەڵی داتا (Excel)</h1>
      <p style={styles.sub}>فایلێکی Excel بار بکە بۆ زیادکردن یان نوێکردنەوەی چەندین فەرمانبەر بە یەکجار. ئەگەر UPN ـی فەرمانبەرێک پێشتر هەبووبێت، تەنها زانیارییەکانی نوێ دەکرێتەوە — فۆرمی پێشووی دەستکاری ناکرێت.</p>

      <div style={styles.steps}>
        {['١. هەڵبژاردنی فایل', '٢. ڕێکخستنی ستوونەکان', '٣. ئەنجام'].map((s, i) => (
          <div key={i} style={{...styles.stepItem, ...(step === i+1 ? styles.stepActive : step > i+1 ? styles.stepDone : {})}}>
            {s}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div style={styles.card}>
          <div style={styles.dropZone} onClick={() => fileRef.current?.click()}>
            <div style={{fontSize:40}}>📊</div>
            <div style={{fontWeight:'bold', marginTop:8}}>کلیک بکە بۆ هەڵبژاردنی فایلی Excel</div>
            <div style={{fontSize:12, color:'#888', marginTop:4}}>.xlsx یان .xls</div>
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFile} style={{display:'none'}} />
        </div>
      )}

      {step === 2 && (
        <div style={styles.card}>
          <div style={{marginBottom:14, fontSize:13, color:'#555'}}>
            📄 فایل: <strong>{fileName}</strong> — {rawRows.length} ڕیز دۆزرایەوە
          </div>

          <Field label="بەشی ئەم داتایانە (هەموو فەرمانبەرەکان دەچنە ناو ئەم بەشە)" required>
            <select style={fstyles.input} value={departmentId} onChange={e => setDepartmentId(e.target.value)}>
              <option value="">--- بەش هەڵبژێرە ---</option>
              {depts.map(d => <option key={d.id} value={d.id}>{d.name_ku} ({d.code})</option>)}
            </select>
          </Field>

          <h3 style={{fontSize:13, fontWeight:'bold', color:'#1a5276', margin:'16px 0 8px'}}>ستوونی فایلەکەت بۆ کام خانە دادەنرێت؟</h3>
          <div style={styles.mapGrid}>
            {FIELDS.map(f => (
              <Field key={f.key} label={f.label} required={f.required}>
                <select style={fstyles.input} value={mapping[f.key] ?? ''}
                  onChange={e => setMapping({...mapping, [f.key]: e.target.value === '' ? undefined : parseInt(e.target.value)})}>
                  <option value="">--- هیچ ---</option>
                  {headers.map((h, i) => <option key={i} value={i}>{h || `ستوونی ${i+1}`}</option>)}
                </select>
              </Field>
            ))}
          </div>

          <h3 style={{fontSize:13, fontWeight:'bold', color:'#1a5276', margin:'16px 0 8px'}}>
            پێشبینین (یەکەم ٥ ڕیز) — {validCount} لە {mappedRows.length} ڕیز دروستە
          </h3>
          <div style={{overflowX:'auto'}}>
            <table style={ptbl.table}>
              <thead>
                <tr>{FIELDS.map(f => <th key={f.key} style={ptbl.th}>{f.label}</th>)}</tr>
              </thead>
              <tbody>
                {mappedRows.slice(0,5).map((r, i) => {
                  const valid = /^\d{13}$/.test(r.upn) && r.full_name;
                  return (
                    <tr key={i} style={{background: valid ? '#fff' : '#fdecea'}}>
                      {FIELDS.map(f => <td key={f.key} style={ptbl.td}>{r[f.key] || '—'}</td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={styles.btnRow}>
            <button onClick={submit} disabled={!canProceedMapping || submitting} style={styles.btnBlue}>
              {submitting ? 'تۆمارکردن...' : `✅ تۆمارکردنی ${validCount} ڕیز`}
            </button>
            <button onClick={reset} style={styles.btnGray}>❌ هەڵوەشاندنەوە</button>
          </div>
          {!canProceedMapping && (
            <div style={{fontSize:12, color:'#e74c3c', marginTop:8}}>
              ⚠️ پێویستە بەش هەڵبژێریت و خانەکانی UPN و ناوی فەرمانبەر دیاری بکەیت
            </div>
          )}
        </div>
      )}

      {step === 3 && result && (
        <div style={styles.card}>
          <div style={styles.resultGrid}>
            <div style={{...styles.resultCard, borderTop:'4px solid #1e8449'}}>
              <div style={{fontSize:24, fontWeight:'bold', color:'#1e8449'}}>{result.created}</div>
              <div style={{fontSize:12, color:'#888'}}>فۆرمی نوێ دروستکرا</div>
            </div>
            <div style={{...styles.resultCard, borderTop:'4px solid #1a5276'}}>
              <div style={{fontSize:24, fontWeight:'bold', color:'#1a5276'}}>{result.updated}</div>
              <div style={{fontSize:12, color:'#888'}}>زانیاری نوێکرایەوە (UPN پێشوو)</div>
            </div>
            <div style={{...styles.resultCard, borderTop:'4px solid #e74c3c'}}>
              <div style={{fontSize:24, fontWeight:'bold', color:'#e74c3c'}}>{result.skipped}</div>
              <div style={{fontSize:12, color:'#888'}}>پشتگوێخراو (هەڵە)</div>
            </div>
          </div>

          {result.errors?.length > 0 && (
            <div style={{marginTop:16}}>
              <h3 style={{fontSize:13, fontWeight:'bold', color:'#e74c3c', marginBottom:8}}>وردەکاری هەڵەکان:</h3>
              <div style={{maxHeight:200, overflowY:'auto', border:'1px solid #eee', borderRadius:6}}>
                {result.errors.map((e, i) => (
                  <div key={i} style={{padding:'6px 10px', fontSize:12, borderBottom:'1px solid #f5f5f5'}}>
                    ڕیزی {e.row}: {e.reason}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={styles.btnRow}>
            <button onClick={reset} style={styles.btnBlue}>📤 ئەپلۆدی فایلێکی تر</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={fstyles.field}>
      <label style={fstyles.label}>{label}{required && <span style={{color:'red'}}> *</span>}</label>
      {children}
    </div>
  );
}

const styles = {
  page: { padding:20, maxWidth:1000 },
  title: { fontSize:20, fontWeight:'bold', color:'#1a5276', margin:'0 0 6px' },
  sub: { fontSize:13, color:'#666', marginBottom:20, lineHeight:1.6 },
  steps: { display:'flex', gap:8, marginBottom:20 },
  stepItem: { flex:1, textAlign:'center', padding:'8px 10px', borderRadius:8, background:'#f0f0f0', fontSize:12, color:'#888', fontWeight:'bold' },
  stepActive: { background:'#1a5276', color:'#fff' },
  stepDone: { background:'#1e8449', color:'#fff' },
  card: { background:'#fff', borderRadius:10, padding:24, boxShadow:'0 2px 8px rgba(0,0,0,0.08)' },
  dropZone: { border:'2px dashed #1a5276', borderRadius:10, padding:'40px 20px', textAlign:'center', cursor:'pointer', background:'#f8fafc' },
  mapGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:12 },
  btnRow: { display:'flex', gap:10, marginTop:20 },
  btnBlue: { background:'#1a5276', color:'#fff', border:'none', borderRadius:6, padding:'10px 20px', cursor:'pointer', fontSize:13, fontWeight:'bold' },
  btnGray: { background:'#f0f0f0', color:'#444', border:'1px solid #ddd', borderRadius:6, padding:'10px 20px', cursor:'pointer', fontSize:13 },
  resultGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:14 },
  resultCard: { background:'#f8fafc', borderRadius:8, padding:16, textAlign:'center' },
};
const fstyles = {
  field: { display:'flex', flexDirection:'column', gap:4, marginBottom:10 },
  label: { fontSize:12, fontWeight:'bold', color:'#555' },
  input: { padding:'8px 10px', border:'1.5px solid #ddd', borderRadius:6, fontSize:13, fontFamily:'inherit' },
};
const ptbl = {
  table: { width:'100%', borderCollapse:'collapse', fontSize:11 },
  th: { background:'#1a5276', color:'#fff', padding:'6px 8px', textAlign:'center', whiteSpace:'nowrap' },
  td: { padding:'5px 8px', border:'1px solid #eee', textAlign:'center', whiteSpace:'nowrap' },
};
